// client/src/components/pages/LexiStar/FleetTargets/FleetTargets.tsx
//
// LexiStar · Analyse → Fleet & targets (route /lexistar/fleet-performance, page permission
// lexistar.kpis.view; setting targets needs lexistar.reports.manage, in-page):
//   - Availability & utilisation (ST-00247): per truck and per day / week / month, on the ST-00249
//     time model split by the organisation's own trips (AvailabilityView);
//   - Actual vs target (ST-00248): each target against its actual per period (ProgressView);
//   - Targets (ST-00248): the organisation's targets, added, changed from a day, ended and retired
//     (TargetsEditor).
// One filter row above everything it scopes: the period (the shared PeriodPicker over
// UnifiedDateRangePicker) and the step (day / week / month). Tab, period, step and sort live in
// the URL so a view can be shared. While a new slice loads the previous one stays, dimmed.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaBullseye, FaSyncAlt } from 'react-icons/fa';
import { SegmentedControl, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { ErrorBanner, NoPermissionPage, NoticeBanner, PageHeader } from '../StatusBoard/atoms';
import { PeriodPicker, SkeletonBlock } from '../Dashboard/analyticsAtoms';
import { dayLabel, grainFor, periodLabel, readPeriod } from '../Dashboard/periodLogic';
import {
  createTarget,
  getFleetAvailability,
  getTargetProgress,
  getTargetScopes,
  listTargets,
  retireTarget,
  reviseTarget,
  updateTarget,
} from '../../../../services/lexistarPerformance';
import type {
  FleetAvailabilityResponse,
  FleetGrain,
  TargetInput,
  TargetList,
  TargetMetricKey,
  TargetPatch,
  TargetProgressResponse,
  TargetRetire,
  TargetRevision,
  TargetScopes,
  TargetScopeType,
} from '../../../../types/lexistar-performance';
import AvailabilityView from './AvailabilityView';
import ProgressView from './ProgressView';
import TargetsEditor, { apiErrorText } from './TargetsEditor';
import { isTruckSort, type TruckSort } from './fleetLogic';

export type TabId = 'fleet' | 'progress' | 'targets';
const TABS: Array<{ value: TabId; label: string; shortLabel: string }> = [
  { value: 'fleet', label: 'Availability & utilisation', shortLabel: 'Fleet' },
  { value: 'progress', label: 'Actual vs target', shortLabel: 'Actual' },
  { value: 'targets', label: 'Targets', shortLabel: 'Targets' },
];
const isTab = (v: unknown): v is TabId => v === 'fleet' || v === 'progress' || v === 'targets';
const isGrain = (v: unknown): v is FleetGrain => v === 'day' || v === 'week' || v === 'month';
const GRAINS: Array<{ value: FleetGrain; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

export default function FleetTargets() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.kpis.view');
  const canManage = hasPermission('lexistar.reports.manage');
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: TabId = isTab(tabParam) ? tabParam : 'fleet';
  const period = readPeriod(params);
  const grainParam = params.get('grain');
  const grain: FleetGrain = isGrain(grainParam) ? grainParam : grainFor(period);
  const sortParam = params.get('sort');
  const sort: TruckSort = isTruckSort(sortParam) ? sortParam : 'truck';
  const metricParam = params.get('metric') || '';
  const scopeParam = params.get('applies') || '';

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  const [fleet, setFleet] = useState<FleetAvailabilityResponse | null>(null);
  const [progress, setProgress] = useState<TargetProgressResponse | null>(null);
  const [targets, setTargets] = useState<TargetList | null>(null);
  const [scopes, setScopes] = useState<TargetScopes | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = () => setTick((t) => t + 1);

  const key = `${tab}|${period.from}|${period.to}|${grain}|${metricParam}|${scopeParam}|${tick}`;
  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    const work: Array<Promise<unknown>> = [];
    if (tab === 'fleet') {
      work.push(getFleetAvailability({ from: period.from, to: period.to, grain }).then((r) => alive && setFleet(r)));
    } else {
      work.push(listTargets().then((r) => alive && setTargets(r)));
      if (tab === 'progress') {
        work.push(
          getTargetProgress({
            from: period.from,
            to: period.to,
            grain,
            ...(metricParam ? { metric: [metricParam as TargetMetricKey] } : {}),
            ...(scopeParam ? { scope_type: scopeParam as TargetScopeType } : {}),
          }).then((r) => alive && setProgress(r))
        );
      }
      if (tab === 'targets' && canManage && !scopes) {
        work.push(getTargetScopes().then((r) => alive && setScopes(r)).catch(() => undefined));
      }
    }
    Promise.all(work)
      .catch((err) => alive && setError(apiErrorText(err, 'The figures could not be loaded.')))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // `key` carries the inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, key]);

  const header = (
    <PageHeader
      icon={FaBullseye}
      title="Fleet & targets"
      subtitle="How available and how used each truck was, and the actual figures against the targets you set."
      toolbar={canView && tab !== 'targets' ? <PeriodPicker period={period} onChange={(p) => update({ from: p.from, to: p.to })} /> : null}
      actions={
        canView ? (
          <button
            type="button"
            onClick={reload}
            aria-label="Refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        ) : null
      }
      inline={false}
    />
  );
  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view the fleet figures and targets." />;

  const metrics = targets?.metrics || [];
  const done = (message: string) => {
    setNotice(message);
    reload();
  };
  const onCreate = async (input: TargetInput) => {
    await createTarget(input);
    done('Target saved.');
  };
  const onRevise = async (id: string, body: TargetRevision) => {
    const r = await reviseTarget(id, body);
    done(
      r.previous && r.previous.effective_to
        ? `New target from ${dayLabel(r.target.effective_from)}; the earlier one ends on ${dayLabel(r.previous.effective_to)}.`
        : 'Target corrected.'
    );
    return r;
  };
  const onUpdate = async (id: string, patch: TargetPatch) => {
    await updateTarget(id, patch);
    done('Target updated.');
  };
  const onRetire = async (id: string, body: TargetRetire) => {
    await retireTarget(id, body);
    done('Target retired.');
  };

  const shownData = tab === 'fleet' ? fleet : tab === 'progress' ? progress : targets;
  const windowText = tab === 'fleet' && fleet ? periodLabel({ from: fleet.from, to: fleet.to }) : tab === 'progress' && progress ? periodLabel({ from: progress.from, to: progress.to }) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <SegmentedControl<TabId> ariaLabel="View" value={tab} onChange={(t) => update({ tab: t === 'fleet' ? null : t })} options={TABS} />
          {tab !== 'targets' && (
            <SegmentedControl<FleetGrain> ariaLabel="Step" value={grain} onChange={(g) => update({ grain: g })} options={GRAINS} />
          )}
          {tab === 'progress' && (
            <>
              <div className="w-48">
                <ThemedSelect aria-label="Metric" value={metricParam} onChange={(e) => update({ metric: String(e.target.value) || null })}>
                  <option value="">Every metric</option>
                  {metrics.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
              <div className="w-48">
                <ThemedSelect aria-label="Applies to" value={scopeParam} onChange={(e) => update({ applies: String(e.target.value) || null })}>
                  <option value="">Everything</option>
                  <option value="tenant">Whole organisation</option>
                  <option value="route">Routes</option>
                  <option value="asset">Trucks</option>
                </ThemedSelect>
              </div>
            </>
          )}
        </div>
        {windowText && (
          <p className="text-xs text-slate-500">
            Whole {grain === 'day' ? 'days' : grain === 'week' ? 'weeks' : 'months'} from {windowText}, counted up to now.
          </p>
        )}
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
        {!shownData && loading ? (
          <div className="space-y-3" aria-busy="true">
            <SkeletonBlock className="h-24" />
            <SkeletonBlock className="h-64" />
          </div>
        ) : (
          <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'} aria-busy={loading}>
            {tab === 'fleet' && fleet && <AvailabilityView res={fleet} grain={fleet.grain} sort={sort} onSort={(s) => update({ sort: s === 'truck' ? null : s })} />}
            {tab === 'progress' && progress && (
              <ProgressView res={progress} metrics={metrics} canManage={canManage} onSetTargets={() => update({ tab: 'targets' })} />
            )}
            {tab === 'targets' && targets && (
              <TargetsEditor
                targets={targets.targets}
                metrics={metrics}
                scopes={scopes}
                today={targets.today}
                canManage={canManage}
                onCreate={onCreate}
                onRevise={onRevise}
                onUpdate={onUpdate}
                onRetire={onRetire}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
